import {
  Badge,
  Box,
  Center,
  Flex,
  Text as ChakraText,
  Tooltip,
} from "@chakra-ui/react";
import { chakra } from "@chakra-ui/system";
import { Group } from "@visx/group";
import { scaleTime } from "@visx/scale";
import { Bar, Line } from "@visx/shape";
import { Text } from "@visx/text";
import { GenericTableBadgeTypeEnum } from "gen/model/genericTableBadgeTypeEnum";
import { GenericTimelineData } from "gen/model/genericTimelineData";
import { GenericTimelineRowMetadata } from "gen/model/genericTimelineRowMetadata";
import { first, last, noop } from "lodash-es";
import React, { useMemo, useRef } from "react";
import { TimelineConstants } from "shared/components/timeline/TimelineConstants";
import {
  useRowsLayout,
  useTimelineFlagsLayout,
} from "shared/components/timeline/TimelineLayout";
import invariant from "shared/utils/TinyInvariant";
import { textStyles } from "theme/textStyles";
import { useResizeObserver } from "usehooks-ts";

export interface TimelineProps {
  data: GenericTimelineData;
}

const colorsForType: Record<GenericTimelineRowMetadata["type"], string> = {
  commitment: "var(--chakra-colors-teal-200)",
  fund_family: "var(--chakra-colors-gray-200)",
  investor_class: "var(--chakra-colors-blue-200)",
  fund: "var(--chakra-colors-teal-200)",
};

const badgeColorsForType: Record<
  GenericTimelineRowMetadata["type"],
  GenericTableBadgeTypeEnum
> = {
  commitment: "teal",
  fund_family: "default",
  investor_class: "blue",
  fund: "teal",
};

const {
  rowHeight,
  rowSpacing,
  xPadding,
  yPadding,
  badgeAreaWidth,
  barStrokeWidth,
  barTextMinWidth,
} = TimelineConstants;

export const Timeline: React.FC<TimelineProps> = ({ data }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { width } = useResizeObserver({ ref });

  return (
    <Box ref={ref} w="100%">
      {width ? <TimelineSvg width={width} data={data} /> : null}
    </Box>
  );
};

const TimelineSvg: React.FC<{
  width: number;
  data: GenericTimelineData;
}> = ({ width, data }) => {
  const { minDate, maxDate } = useMinMaxDates(data);

  const startX = badgeAreaWidth + xPadding;
  const timelineWidth = width - startX - xPadding;

  const xScale = scaleTime({
    range: [0, timelineWidth],
    domain: [minDate, maxDate],
  });

  const { flagRows, startY } = useTimelineFlagsLayout(
    data,
    timelineWidth,
    xScale,
    yPadding,
  );
  const rowsLayout = useRowsLayout(data, startX, xScale);

  const height =
    data.rows.length * (rowHeight + rowSpacing) -
    rowSpacing +
    yPadding +
    startY;

  return (
    <chakra.svg w="100%" h={`${height}px`}>
      <Group top={startY} left={xPadding}>
        {rowsLayout.map(({ row, badge }, rowIndex) => (
          <foreignObject key={rowIndex} {...badge}>
            <Flex
              w="100%"
              h="100%"
              pr="18px"
              alignItems="center"
              justifyContent="flex-end"
            >
              <Badge
                variant="category"
                colorScheme={badgeColorsForType[row.metadata.type]}
                onClick={noop}
              >
                {row.label}
              </Badge>
            </Flex>
          </foreignObject>
        ))}
      </Group>
      <Group top={startY} left={startX}>
        {rowsLayout.map(({ row, periods }, rowIndex) => (
          <Group key={rowIndex}>
            {periods.map(({ bar }, periodIndex) => (
              <Bar
                key={`${rowIndex}-${periodIndex}`}
                {...bar}
                fill={colorsForType[row.metadata.type]}
                stroke="white"
                strokeWidth={barStrokeWidth}
                rx={4}
              />
            ))}
          </Group>
        ))}
      </Group>
      <Group top={yPadding} left={startX}>
        {flagRows.map((flagRow, flagRowIndex) => (
          <Group key={flagRowIndex}>
            {flagRow.map((flagLayout, flagIndex) => (
              <React.Fragment key={`${flagRowIndex}-${flagIndex}`}>
                <Bar
                  {...flagLayout.bar}
                  fill="var(--chakra-colors-gray-100)"
                  rx={4}
                />
                <Line
                  {...flagLayout.line}
                  stroke="var(--chakra-colors-gray-100)"
                />
                <Text
                  {...flagLayout.text}
                  fontWeight={textStyles.small.fontWeight as string}
                  fontSize={textStyles.small.fontSize as string}
                  textAnchor="middle"
                  verticalAnchor="middle"
                >
                  {flagLayout.flag.label}
                </Text>
              </React.Fragment>
            ))}
          </Group>
        ))}
      </Group>
      <Group top={startY} left={startX}>
        {rowsLayout.map(({ periods }, rowIndex) => (
          <Group key={rowIndex}>
            {periods.map(({ period, bar }, periodIndex) => (
              <foreignObject key={`${rowIndex}-${periodIndex}`} {...bar}>
                <Tooltip label={period.label} openDelay={0} closeDelay={0}>
                  <Center w="100%" h="100%" px="8px">
                    <ChakraText
                      textAlign="center"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      display={bar.width < barTextMinWidth ? "none" : "block"}
                    >
                      {period.label}
                    </ChakraText>
                  </Center>
                </Tooltip>
              </foreignObject>
            ))}
          </Group>
        ))}
      </Group>
    </chakra.svg>
  );
};

const useMinMaxDates = (data: GenericTimelineData) =>
  useMemo(() => {
    const allDates = data.rows
      .flatMap((d) => d.periods.flatMap((p) => [p.start_at, p.end_at]))
      .toSorted()
      .map((d) => new Date(d));
    invariant(allDates.length > 0, "No dates found in timeline data");
    const minDate = first(allDates)!;
    const maxDate = last(allDates)!;
    return { minDate, maxDate } as const;
  }, [data]);
